import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getAuthHeader } from '../../../utils/auth';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, Users, MapPin } from 'lucide-react';
import { format, startOfWeek, endOfWeek, addWeeks, subWeeks, addDays, isSameDay, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import VisitStatusBadge from '../../../components/production/VisitStatusBadge';
import '../../../styles/admin/production/CalendarView.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CalendarView = () => {
  const [currentWeek, setCurrentWeek] = useState(new Date());
  const [teams, setTeams] = useState([]);
  const [allVisits, setAllVisits] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [currentWeek]);

  const fetchData = async () => {
    try {
      const [teamsRes, jobsRes] = await Promise.all([
        axios.get(`${API_URL}/api/teams`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/jobs?limit=500`, { headers: getAuthHeader() })
      ]);

      setTeams(teamsRes.data.filter(t => t.status === 'active'));
      setJobs(jobsRes.data);

      // Fetch all visits for all jobs
      const jobIds = jobsRes.data.map(j => j.id);
      const visitsPromises = jobIds.map(jobId =>
        axios.get(`${API_URL}/api/job-visits/job/${jobId}`, { headers: getAuthHeader() })
          .catch(() => ({ data: [] }))
      );

      const visitsResults = await Promise.all(visitsPromises);
      const allVisitsFlat = visitsResults.flatMap(res => res.data);

      // Filter visits for current week
      const weekStart = startOfWeek(currentWeek, { locale: fr, weekStartsOn: 1 });
      const weekEnd = endOfWeek(currentWeek, { locale: fr, weekStartsOn: 1 });

      const visitsInWeek = allVisitsFlat.filter(visit => {
        if (!visit.scheduled_start) return false;
        const visitDate = parseISO(visit.scheduled_start);
        return visitDate >= weekStart && visitDate <= weekEnd;
      });

      setAllVisits(visitsInWeek);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      setLoading(false);
    }
  };

  const getWeekDays = () => {
    const weekStart = startOfWeek(currentWeek, { locale: fr, weekStartsOn: 1 });
    return Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  };

  const getVisitsForTeamAndDay = (teamId, day) => {
    return allVisits.filter(visit => {
      if (visit.team_id !== teamId) return false;
      if (!visit.scheduled_start) return false;
      const visitDate = parseISO(visit.scheduled_start);
      return isSameDay(visitDate, day);
    });
  };

  const getJobForVisit = (visitJobId) => {
    return jobs.find(j => j.id === visitJobId);
  };

  const handlePrevWeek = () => {
    setCurrentWeek(subWeeks(currentWeek, 1));
  };

  const handleNextWeek = () => {
    setCurrentWeek(addWeeks(currentWeek, 1));
  };

  const handleToday = () => {
    setCurrentWeek(new Date());
  };

  if (loading) {
    return <div className="calendar-loading">Chargement du calendrier...</div>;
  }

  const weekDays = getWeekDays();
  const weekStart = startOfWeek(currentWeek, { locale: fr, weekStartsOn: 1 });
  const weekEnd = endOfWeek(currentWeek, { locale: fr, weekStartsOn: 1 });

  return (
    <div className="calendar-container">
      {/* Header */}
      <div className="calendar-header">
        <div className="calendar-title">
          <CalendarIcon size={24} />
          <h2>Calendrier des visites</h2>
        </div>
        <div className="calendar-navigation">
          <button onClick={handlePrevWeek} className="btn-nav">
            <ChevronLeft size={20} />
          </button>
          <button onClick={handleToday} className="btn-today">
            Aujourd'hui
          </button>
          <button onClick={handleNextWeek} className="btn-nav">
            <ChevronRight size={20} />
          </button>
        </div>
        <div className="calendar-week-label">
          {format(weekStart, 'dd MMM', { locale: fr })} - {format(weekEnd, 'dd MMM yyyy', { locale: fr })}
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="calendar-scroll-container">
        <div className="calendar-grid">
          {/* Header Row - Days */}
          <div className="calendar-grid-header">
            <div className="calendar-team-header">Équipes ({teams.length})</div>
            {weekDays.map(day => {
              const isToday = isSameDay(day, new Date());
              return (
                <div key={day.toString()} className={`calendar-day-header ${isToday ? 'today' : ''}`}>
                  <div className="day-name">{format(day, 'EEE', { locale: fr })}</div>
                  <div className="day-number">{format(day, 'd')}</div>
                </div>
              );
            })}
          </div>

          {/* Team Rows */}
          {teams.length === 0 ? (
            <div className="calendar-empty">
              <Users size={48} />
              <p>Aucune équipe active</p>
            </div>
          ) : (
            teams.map(team => (
              <div key={team.id} className="calendar-team-row">
                <div className="calendar-team-cell">
                  <div className="team-name">{team.name}</div>
                  <div className="team-code">{team.code}</div>
                  {team.employee_ids && (
                    <div className="team-members">{team.employee_ids.length} membre(s)</div>
                  )}
                </div>
                {weekDays.map(day => {
                  const visits = getVisitsForTeamAndDay(team.id, day);
                  const isToday = isSameDay(day, new Date());
                  
                  return (
                    <div key={day.toString()} className={`calendar-day-cell ${isToday ? 'today' : ''}`}>
                      {visits.length === 0 ? (
                        <div className="no-visits">-</div>
                      ) : (
                        <div className="visits-in-day">
                          {visits.map(visit => {
                            const job = getJobForVisit(visit.job_id);
                            const startTime = visit.scheduled_start ? 
                              format(parseISO(visit.scheduled_start), 'HH:mm', { locale: fr }) : 
                              '??:??';

                            return (
                              <div 
                                key={visit.id} 
                                className="visit-block"
                                title={`${job?.title || 'Job'} - ${startTime}`}
                              >
                                <div className="visit-time">{startTime}</div>
                                <div className="visit-job">
                                  {job?.job_number || 'N/A'}
                                </div>
                                <div className="visit-location">
                                  <MapPin size={10} />
                                  {job?.service_address?.city || 'N/A'}
                                </div>
                                <VisitStatusBadge status={visit.status} />
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ))
          )}
        </div>
      </div>

      {/* Summary */}
      <div className="calendar-summary">
        <div className="summary-item">
          <strong>{allVisits.length}</strong> visite(s) cette semaine
        </div>
        <div className="summary-item">
          <strong>{allVisits.filter(v => v.status === 'scheduled').length}</strong> planifiée(s)
        </div>
        <div className="summary-item">
          <strong>{allVisits.filter(v => v.status === 'in_progress').length}</strong> en cours
        </div>
        <div className="summary-item">
          <strong>{allVisits.filter(v => v.status === 'completed').length}</strong> complétée(s)
        </div>
      </div>
    </div>
  );
};

export default CalendarView;
